// ============================================
// CUSTOM HOOKS
// ============================================

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/auth';
import { authApi, companiesApi, jobsApi, applicationsApi, notificationsApi, usersApi, matchingApi, favoritesApi, supportApi } from '@/services/api';
import type { LoginRequest } from '@crew-at-sea/shared-types';
import type { UpdateNotificationSettingsData, SupportRequest } from '@crew-at-sea/api-client';
import { i18n } from '@crew-at-sea/i18n';

// ============================================
// AUTH HOOKS
// ============================================

// API client 401'i onUnauthorized'a düşürüyor — login için doğrudan fetch kullan
const API_URL = import.meta.env.VITE_API_URL || 'https://api.crewatsea.com/api/v1';

const t = (key: string) => i18n.t(key);


export function useLogin() {
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      try {
        // Doğrudan fetch — API client 401'i onUnauthorized'a düşürmesin
        const res = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        let json: any = {};
        try { json = await res.json(); } catch { /* JSON parse hatası */ }

        if (!res.ok) {
          // Backend message: string | string[] | object olabilir
          const rawMsg = json?.error?.message || json?.message;
          let msgStr = 'Kullanıcı bilgileri hatalı';
          if (typeof rawMsg === 'string' && rawMsg.length > 0) {
            msgStr = rawMsg;
          } else if (Array.isArray(rawMsg) && rawMsg.length > 0) {
            msgStr = rawMsg.map((m: any) => String(m)).join(', ');
          } else if (typeof json?.error === 'string') {
            msgStr = json.error;
          }

          // Çeviri karşılığı bul — güvenli string karşılaştırma
          let friendly = '';
          const lower = msgStr.toLowerCase();
          
          if (lower.includes('invalid') || lower.includes('unauthorized') || lower.includes('incorrect') || lower.includes('wrong')) {
            friendly = t('auth:login.errors.invalidCredentials');
          } else if (lower.includes('suspended')) {
            friendly = t('auth:login.errors.accountSuspended');
          } else if (lower.includes('inactive')) {
            friendly = t('auth:login.errors.accountInactive');
          } else if (lower.includes('not found')) {
            friendly = t('auth:login.errors.userNotFound');
          } else {
            friendly = msgStr;
          }

          // Fallback if translations are missing
          if (friendly === 'auth:login.errors.invalidCredentials') friendly = 'Kullanıcı bilgileri hatalı. Lütfen e-posta ve şifrenizi kontrol edin.';
          if (friendly === 'auth:login.errors.accountInactive') friendly = 'Hesabınız aktif değil. E-posta doğrulamanızı kontrol edin.';
          if (friendly === 'auth:login.errors.accountSuspended') friendly = 'Hesabınız askıya alınmış. Destek için iletişime geçin.';
          if (friendly === 'auth:login.errors.userNotFound') friendly = 'Bu e-posta adresiyle kayıtlı hesap bulunamadı.';

          throw new Error(friendly);
        }

        const result = json?.data || json;
        const { user, accessToken, refreshToken } = result || {};

        if (!user) throw new Error('Geçersiz sunucu yanıtı. Lütfen tekrar deneyin.');

        if (user.role !== 'COMPANY') {
          throw new Error('Bu panel sadece şirket hesapları içindir. Denizci iseniz user.crewatsea.com adresini kullanın.');
        }

        const company = user.company || null;
        
        const isEmailVerified = !!user.emailVerified;
        if (!isEmailVerified) {
          throw new Error('Giriş engellendi: Hesabınıza erişebilmek için e-posta doğrulamanızın yapılmış olması gerekmektedir.');
        }

        login({ id: user.id, email: user.email, role: 'COMPANY', company }, accessToken, refreshToken);
        toast.success('Giriş başarılı!');
        setTimeout(() => { window.location.href = '/'; }, 100);
      } catch (err: any) {
        // İçeride fırlatılan Error'ları olduğu gibi ilet
        // Başka runtime hatası varsa genel mesaj ver
        if (err instanceof Error) throw err;
        throw new Error('Kullanıcı bilgileri hatalı. Lütfen tekrar deneyin.');
      }
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { logout, refreshToken } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(refreshToken || undefined),
    onSettled: () => {
      logout();
      queryClient.clear();
      toast.success('Çıkış yapıldı');
      navigate({ to: '/login' });
    },
  });
}

// ============================================
// COMPANY HOOKS
// ============================================

export function useMyCompany() {
  return useQuery({
    queryKey: ['company', 'me'],
    queryFn: () => companiesApi.getMyCompany(),
  });
}

export function useCompanyStats() {
  return useQuery({
    queryKey: ['company', 'stats'],
    queryFn: () => companiesApi.getStats(),
  });
}

export function useCompanyAnalytics(period: string = '30d') {
  return useQuery({
    queryKey: ['company', 'analytics', period],
    queryFn: () => companiesApi.getAnalytics(period),
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => companiesApi.updateMyCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      toast.success('Şirket bilgileri güncellendi');
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) => 
      companiesApi.uploadLogo(file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      toast.success('Logo yüklendi');
    },
  });
}

export function useUploadBanner() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) => 
      companiesApi.uploadBanner(file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      toast.success('Banner yüklendi');
    },
  });
}

export function useDeleteLogo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => companiesApi.deleteLogo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      toast.success('Logo silindi');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Logo silinemedi');
    },
  });
}

export function useDeleteBanner() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => companiesApi.deleteBanner(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      toast.success('Banner silindi');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Banner silinemedi');
    },
  });
}

// ============================================
// JOB HOOKS
// ============================================

export function useMyJobs(params?: { page?: number; limit?: number; status?: string; department?: string }) {
  return useQuery({
    queryKey: ['jobs', 'me', params],
    queryFn: () => jobsApi.getMyJobs(params as any),
  });
}

export function useJob(slug: string) {
  return useQuery({
    queryKey: ['jobs', slug],
    queryFn: () => jobsApi.getBySlug(slug),
    enabled: !!slug,
  });
}

export function useCreateJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => jobsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('İş ilanı oluşturuldu');
    },
  });
}

export function useUpdateJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      jobsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('İş ilanı güncellendi');
    },
  });
}

export function useUpdateJobStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      jobsApi.updateStatus(id, { status } as any),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      const statusMessages: Record<string, string> = {
        ACTIVE: 'İlan aktifleştirildi',
        PAUSED: 'İlan duraklatıldı',
        CLOSED: 'İlan kapatıldı',
        DRAFT: 'İlan taslağa alındı',
      };
      toast.success(statusMessages[variables.status] || 'İlan durumu güncellendi');
    },
  });
}

// 12.7 Delete Job
export function useDeleteJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => jobsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['company', 'stats'] });
      toast.success('İlan silindi');
    },
  });
}

// ============================================
// APPLICATION HOOKS
// ============================================

export function useJobApplications(jobId: string) {
  return useQuery({
    queryKey: ['applications', 'job', jobId],
    queryFn: () => jobsApi.getApplications(jobId),
    enabled: !!jobId,
  });
}

export function useUpdateApplicationStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      applicationsApi.updateStatus(id, { status: status as any, notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      const statusMessages: Record<string, string> = {
        REVIEWED: 'Başvuru incelendi olarak işaretlendi',
        SHORTLISTED: 'Aday ön elemeye alındı',
        INTERVIEW: 'Aday mülakata alındı',
        OFFERED: 'Adaya teklif yapıldı',
        ACCEPTED: 'Başvuru kabul edildi',
        REJECTED: 'Başvuru reddedildi',
      };
      toast.success(statusMessages[variables.status] || 'Başvuru durumu güncellendi');
    },
  });
}

// 12.4 Update Application Notes
export function useUpdateApplicationNotes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      applicationsApi.updateNotes(id, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      toast.success('Notlar kaydedildi');
    },
  });
}

// ============================================
// UI HOOKS
// ============================================

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function usePagination(initialPage: number = 1, initialLimit: number = 10) {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);

  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goToPage = useCallback((p: number) => setPage(p), []);
  const changeLimit = useCallback((l: number) => {
    setLimit(l);
    setPage(1);
  }, []);

  return {
    page,
    limit,
    nextPage,
    prevPage,
    goToPage,
    changeLimit,
    setLimit: changeLimit, // Alias for changeLimit
  };
}

export function useModal(initialState: boolean = false) {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle };
}

// ============================================
// CANDIDATE HOOKS
// ============================================

export function useSeafarerProfile(id: string) {
  return useQuery({
    queryKey: ['seafarers', id],
    queryFn: () => usersApi.getSeafarer(id),
    enabled: !!id,
  });
}

export function useSeafarers(params?: {
  page?: number;
  limit?: number;
  department?: string;
  rank?: string;
  nationality?: string;
  available?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: ['seafarers', params],
    queryFn: () => usersApi.listSeafarers(params),
  });
}

export function useMatchingCandidates(jobId: string) {
  return useQuery({
    queryKey: ['matching', 'candidates', jobId],
    queryFn: () => matchingApi.getMatchingCandidates(jobId),
    enabled: !!jobId,
  });
}

export function useAllMatchingCandidates() {
  return useQuery({
    queryKey: ['matching', 'candidates', 'all'],
    queryFn: () => matchingApi.getAllCandidates(),
  });
}

export function useFavoriteCandidates() {
  return useQuery({
    queryKey: ['favorites', 'candidates'],
    queryFn: () => favoritesApi.getFavoriteCandidates(),
  });
}

export function useAddFavoriteCandidate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { seafarerId: string; notes?: string; tags?: string[] }) =>
      favoritesApi.addFavoriteCandidate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'candidates'] });
      toast.success('Aday favorilere eklendi');
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Favori eklenemedi';
      toast.error(typeof msg === 'string' ? msg : 'Favori eklenemedi');
    },
  });
}

export function useRemoveFavoriteCandidate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (seafarerId: string) => favoritesApi.removeFavoriteCandidate(seafarerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'candidates'] });
      toast.success('Aday favorilerden çıkarıldı');
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Favori kaldırılamadı';
      toast.error(typeof msg === 'string' ? msg : 'Favori kaldırılamadı');
    },
  });
}

export function useUpdateFavoriteCandidate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ seafarerId, notes, tags }: { seafarerId: string; notes?: string; tags?: string[] }) =>
      favoritesApi.updateFavoriteCandidate(seafarerId, { notes, tags }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'candidates'] });
      toast.success('Favori bilgileri güncellendi');
    },
  });
}

// ============================================
// NOTIFICATION HOOKS
// ============================================

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getNotifications(),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 30000, // Poll every 30 seconds
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: async () => {
      const response = await notificationsApi.getSettings();
      // Backend { success: true, data: {...} } formatında dönüyor
      return (response as any)?.data || response;
    },
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: UpdateNotificationSettingsData) => {
      const response = await notificationsApi.updateSettings(data);
      // Backend { success: true, data: {...} } formatında dönüyor
      return (response as any)?.data || response;
    },
    onSuccess: (updatedSettings) => {
      // Cache'i güncellenmiş veri ile güncelle
      queryClient.setQueryData(['notifications', 'settings'], updatedSettings);
      // Ayrıca refetch de yap
      queryClient.invalidateQueries({ queryKey: ['notifications', 'settings'] });
      toast.success('Bildirim ayarları güncellendi');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Ayarlar güncellenemedi');
    },
  });
}

// ============================================
// SUPPORT HOOKS
// ============================================

export function useSendSupportRequest() {
  return useMutation({
    mutationFn: (data: SupportRequest) => supportApi.sendRequest(data),
    onSuccess: () => {
      toast.success('Destek talebiniz gönderildi');
    },
  });
}

export function useDeleteAccount() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => usersApi.deleteAccount(),
    onSuccess: () => {
      logout();
      queryClient.clear();
      toast.success('Hesabınız başarıyla silindi.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    },
  });
}

export function useGoogleLogin() {
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (data: { credential: string; role?: string }) => authApi.googleLogin(data),
    onSuccess: (response: any) => {
      const result = response.data || response;
      const { user, accessToken, refreshToken } = result;
      
      if (!user) {
        throw new Error('Geçersiz sunucu yanıtı.');
      }
      
      const role = user.role;
      if (!role) {
        throw new Error('Kullanıcı rolü bulunamadı.');
      }
      
      if (role === 'COMPANY') {
        const company = user.company || null;
        login(
          { id: user.id, email: user.email, role: 'COMPANY', company },
          accessToken,
          refreshToken
        );
        toast.success('Giriş başarılı!');
        return;
      }
      
      if (role === 'SEAFARER') {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const targetHost = isLocal ? 'http://localhost:3004' : 'https://user.crewatsea.com';
        
        toast.success('Denizci girişi başarılı, yönlendiriliyorsunuz...');
        setTimeout(() => {
          window.location.href = `${targetHost}/login-callback?token=${accessToken}&refresh=${refreshToken}`;
        }, 100);
        return;
      }

      throw new Error('Bu panel sadece şirket hesapları içindir.');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Giriş yapılamadı.');
    },
  });
}
